import { ClientEvents } from 'common/events/ClientEvents';
import React, { useEffect } from 'react';
import { useDishFistCartPositionContext } from 'src/pages/consumer/components/ConsumerHeaderWrapper';
import { ConsumerOrderCartButton } from 'src/pages/consumer/order/components/ConsumerOrderCartButton';
import { DeliveryDateMismatchWarning } from 'src/pages/consumer/order/components/DeliveryDateMismatchWarning';
import { useHelpButtonState } from 'src/shared/design-system/HelpButton';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Routes } from 'src/shared/Routes';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import { useSelector } from 'src/store';
import { selectAvailableCartItemCount } from 'src/store/cart/selectors';
import styled, { css } from 'styled-components';
import { Mixins, Transition } from 'web-common/src/shared/styles';
import { useIsDishFirst } from '../../../../../dish-first/useIsDishFirst';
import { useConsumerCarts } from '../../../consumer-carts/useConsumerCarts';

const name = 'cart';

export const Container = styled.div<{ hasItems?: boolean; isMobileOnly?: boolean }>`
  display: flex;
  flex-direction: row;
  bottom: 0;
  opacity: ${ifProp('hasItems', '1', '0')};
  overflow: hidden;
  pointer-events: ${ifProp('hasItems', 'all', 'none')};
  position: sticky;
  width: 100%;
  transition: opacity ${Transition.Time.BASE};
  flex-direction: column;

  & > a {
    transform: translateY(${ifProp('hasItems', '0', '100%')});
    transition: transform ${Transition.Time.BASE};
  }
`;

const CartButtonContainer = styled.div`
  ${Mixins.Media.minWidthMedium(css`
    display: none;
  `)}
`;
interface ViewCartButtonProps {
  className?: string;
}

export const ViewCartButton: React.FC<ViewCartButtonProps> = ({ className }) => {
  const numItemsInCart = useSelector((state) => selectAvailableCartItemCount(state.cart));
  const { helpButtonIsVisible, show, hide } = useHelpButtonState();
  // Multi shef has a different cart experience than not
  const { isDishFirst } = useIsDishFirst();

  const hasItems = numItemsInCart > 0;

  const cartPositionState = useDishFistCartPositionContext();
  const tracker = useTracker();

  // Shef first has multiple carts, so we go to the multi cart experience
  const route = isDishFirst ? Routes.CONSUMER_ORDER_CART : Routes.CONSUMER_MULTIPLE_CARTS_VIEW;

  const handleCartButtonClick = () => {
    tracker.track(ClientEvents.NAV_CLICK, { elementName: name });

    if (cartPositionState.isCartMounted) {
      cartPositionState.open();
    }
  };

  useEffect(() => {
    if (hasItems && helpButtonIsVisible) {
      hide();
    } else if (!hasItems && !helpButtonIsVisible) {
      show();
    }
  }, [hide, hasItems, show, helpButtonIsVisible]);

  const { carts } = useConsumerCarts();
  const numCarts = carts.length;

  return (
    <Container className={className} hasItems={hasItems}>
      <CartButtonContainer className='w-full border-t border-solid border-neutral-200 bg-white p-3'>
        <div className='mb-2'>{isDishFirst && <DeliveryDateMismatchWarning />}</div>
        <ConsumerOrderCartButton
          className='w-full'
          title={!isDishFirst && numCarts > 1 ? 'View Carts' : 'View Cart'}
          quantity={isDishFirst ? numItemsInCart : numCarts}
          onClick={handleCartButtonClick}
          to={{ pathname: route, search: '' }}
        />
      </CartButtonContainer>
    </Container>
  );
};
